import { FileNameCell } from '@/components/ImportExportHistory/Table/Columns/Cells/FileNameCell';
import { FileSizeCell } from '@/components/ImportExportHistory/Table/Columns/Cells/FileSizeCell';
import { StatusCell } from '@/components/ImportExportHistory/Table/Columns/Cells/StatusCell';
import { TypeCell } from '@/components/ImportExportHistory/Table/Columns/Cells/TypeCell';
import {
  ImportExportHistoryColumn,
  OnCancelClick,
  OnDetailsClick,
  OnDownloadClick,
} from '@/components/ImportExportHistory/type';
import { useTranslation } from '@/i18n';
import { ColumnType } from 'antd/es/table/interface';
import classnames from 'classnames/bind';
import { formatDateWithTimeNoSecond } from 'date-util';
import { useCallback, useMemo } from 'react';
import styles from './column.module.scss';

const cx = classnames.bind(styles);

export const useImportExportHistoryColumns = (actionHandlers?: {
  onDownloadClick: OnDownloadClick;
  onCancelClick: OnCancelClick;
  onDetailsClick?: OnDetailsClick;
}): ColumnType<ImportExportHistoryColumn>[] => {
  const { t } = useTranslation();

  const handleCancelClick = useCallback(
    (id: string) => async () => {
      if (actionHandlers?.onCancelClick) {
        await actionHandlers.onCancelClick(id);
      }
    },
    [actionHandlers]
  );

  const handleDetailsClick = useCallback(
    (id: string) => () => {
      if (actionHandlers?.onDetailsClick) {
        actionHandlers.onDetailsClick(id);
      }
    },
    [actionHandlers]
  );

  return useMemo(
    () => [
      {
        title: t('import_export_history_type'),
        dataIndex: 'type',
        key: 'type',
        width: 104,
        render: (_: unknown, row: ImportExportHistoryColumn) => (
          <TypeCell type={row.type} />
        ),
      },
      {
        title: t('import_export_history_target_data'),
        dataIndex: 'target_data',
        key: 'target_data',
        width: 160,
        className: cx('target-data-column'),
        render: (_: unknown, row: ImportExportHistoryColumn) => (
          <span>{row.target_data}</span>
        ),
      },
      {
        title: t('import_export_history_status'),
        dataIndex: 'status',
        key: 'status',
        width: 176,
        align: 'left',
        className: cx('status-column'),
        render: (_: unknown, row: ImportExportHistoryColumn) => (
          <StatusCell
            status={row.status}
            canCancel={row.can_cancel}
            onCancelClick={handleCancelClick(row.id)}
            onDetailsClick={handleDetailsClick(row.id)}
            type={row.type}
            errorMessage={row.error_message}
            downloadFileId={row.id}
            canDownload={row.can_download}
            onDownloadClick={actionHandlers?.onDownloadClick}
          />
        ),
      },
      {
        title: t('import_export_history_executor'),
        dataIndex: 'office_member',
        key: 'office_member',
        width: 80,
        className: cx('executor-column'),
        render: (_: unknown, row: ImportExportHistoryColumn) => (
          <span>{row.office_member.name}</span>
        ),
      },
      {
        title: t('import_export_history_file_name'),
        dataIndex: 'name',
        key: 'name',
        width: 120,
        className: cx('file-name-column'),
        render: (_: unknown, row: ImportExportHistoryColumn) => (
          <FileNameCell
            row={row}
            actionHandlers={
              actionHandlers
                ? { onDownloadClick: actionHandlers.onDownloadClick }
                : undefined
            }
          />
        ),
      },
      {
        title: t('import_export_history_file_size'),
        dataIndex: 'byte_size',
        key: 'byte_size',
        width: 160,
        align: 'right',
        render: (_: unknown, row: ImportExportHistoryColumn) => (
          <FileSizeCell row={row} />
        ),
      },
      {
        title: t('import_export_history_executed_by'),
        dataIndex: 'executed_by',
        key: 'executed_by',
        width: 128,
        render: (value: string) => formatDateWithTimeNoSecond(value),
      },
      {
        title: t('import_export_history_completed_at'),
        dataIndex: 'completed_at',
        key: 'completed_at',
        width: 128,
        render: (value: string, row: ImportExportHistoryColumn) =>
          row.status === 'processing' || row.status === 'failed'
            ? null
            : formatDateWithTimeNoSecond(value),
      },
    ],
    [t, handleCancelClick, handleDetailsClick, actionHandlers]
  );
};
